import { useState, useEffect } from "react";
import {
  Modal,
  Tabs,
  Upload,
  Button,
  Form,
  Input,
  Select,
  Progress,
  Table,
  Alert,
  Space,
  Divider,
  Card,
  Row,
  Col,
  message,
  Typography,
} from "antd";
import {
  InboxOutlined,
  GoogleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";
import { teamService } from "../services/teamService";
import userService from "../services/userService";
import { useAuth } from "../context/AuthContext";

const { TabPane } = Tabs;
const { Dragger } = Upload;
const { Option } = Select;
const { Text, Title } = Typography;

const BulkQueryImportModal = ({ visible, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("csv");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importStatus, setImportStatus] = useState(null);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [form] = Form.useForm();
  const [googleForm] = Form.useForm();

  // CSV Import State;
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});

  // Google Sheets State;
  const [sheetsConnected, setSheetsConnected] = useState(false);
  const [sheetPreview, setSheetPreview] = useState([]);

  useEffect(() => {
    if (visible) {
      fetchTeamsAndUsers();
    }
  }, [visible]);

  const fetchTeamsAndUsers = async () => {
    try {
      const [teamsRes, usersRes] = await Promise.all([
        teamService.getTeams(),
        userService.getUsers(),
      ]);

      setTeams(teamsRes.data?.data || []);
      setUsers(usersRes.data?.data || []);
    } catch (error) {
      console.error("Error fetching teams/users:", error);
    }
  };

  // CSV Upload Configuration;
  const csvUploadProps = {
    name: "csvFile",
    multiple: false,
    accept: ".csv,.xlsx,.xls",
    beforeUpload: (file) => {
      setCsvFile(file);
      previewCSV(file);
      return false; // Prevent auto upload
    },
    onRemove: () => {
      setCsvFile(null);
      setCsvPreview([]);
      setFieldMapping({});
    },
  };

  const previewCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split("\n").slice(0, 6); // Preview first 5 rows + header
      const preview = lines.map((line) => line.split(","));
      setCsvPreview(preview);

      // Auto-map common fields;
      if (preview.length > 0) {
        const headers = preview[0];
        const autoMapping = {};
        headers.forEach((header, index) => {
          const cleanHeader = header.toLowerCase().trim();
          if (cleanHeader.includes("name") || cleanHeader.includes("client")) {
            autoMapping[index] = "clientName";
          } else if (cleanHeader.includes("email")) {
            autoMapping[index] = "email";
          } else if (cleanHeader.includes("phone")) {
            autoMapping[index] = "phone";
          } else if (cleanHeader.includes("destination")) {
            autoMapping[index] = "destination";
          }
        });
        setFieldMapping(autoMapping);
      }
    };
    reader.readAsText(file);
  };

  const handleCSVImport = async (values) => {
    if (!csvFile) {
      message.error("Please select a CSV file");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("csvFile", csvFile);

const response = await apiClient.post(
  "/data-import/csv/upload",
  formData,
  {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (progressEvent) => {
      const progress = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      setUploadProgress(progress);
    },
  }
);

if (response.data?.importId) {
  setImportStatus({
    type: "success",
    importId: response.data.importId,
    message: "CSV import started successfully",
  });

  pollImportStatus(response.data.importId);
}

    } catch (error) {
      console.error("CSV import error:", error);
      message.error("Failed to import CSV file");
      setImportStatus({
        type: "error",
        message: error.response?.data?.message || "Import failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSheetsConnect = async (values) => {
    try {
      setLoading(true);

const response = await apiClient.post(
  "/data-import/google-sheets/connect",
  {
    spreadsheetId: values.spreadsheetId,
    sheetName: values.sheetName,
    range: values.range,
  }
);     

      if (response.data.importId) {
        setSheetsConnected(true);
        setImportStatus({
          type: "success",
          importId: response.data.importId,
          message: "Google Sheets connected successfully",
        });

        pollImportStatus(response.data.importId);
      }
    } catch (error) {
      console.error("Google Sheets connection error:", error);
      message.error("Failed to connect Google Sheets");
      setImportStatus({
        type: "error",
        message: error.response?.data?.message || "Connection failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const pollImportStatus = async (importId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.get(`/data-import/status/${importId}`);
        const status = response.data;

        if (
          status.status === "completed" ||
          status.status === "failed" ||
          status.status === "partial"
        ) {
          clearInterval(pollInterval);
          setImportStatus({
            type: status.status === "completed" ? "success" : "warning",
            importId: importId,
            message: `Import ${status.status}. Processed: ${status.progress?.successfulRows || 0} queries`,
            details: status,
          });

          if (status.status === "completed") {
            onSuccess(status);
          }
        }
      } catch (error) {
        clearInterval(pollInterval);
        console.error("Error polling import status:", error);
      }
    }, 2000);

    // Clear interval after 5 minutes;
    setTimeout(() => clearInterval(pollInterval), 300000);
  };

  const downloadTemplate = () => {
    const template = [
      [
        "clientName",
        "email",
        "phone",
        "destination",
        "travelDate",
        "returnDate",
        "adults",
        "children",
        "budgetMin",
        "budgetMax",
        "specialRequests",
      ],
      [
        "John Doe",
        "john@example.com",
        "+1234567890",
        "Paris",
        "2024-12-01",
        "2024-12-07",
        "2",
        "0",
        "5000",
        "8000",
        "Honeymoon package",
      ],
      [
        "Jane Smith",
        "jane@example.com",
        "+1234567891",
        "Tokyo",
        "2024-12-15",
        "2024-12-22",
        "4",
        "2",
        "10000",
        "15000",
        "Family vacation",
      ],
    ];

    const csvContent = template.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "query_import_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const fieldOptions = [
    { value: "clientName", label: "Client Name" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "destination", label: "Destination" },
    { value: "travelDate", label: "Travel Date" },
    { value: "returnDate", label: "Return Date" },
    { value: "adults", label: "Adults" },
    { value: "children", label: "Children" },
    { value: "budgetMin", label: "Min Budget" },
    { value: "budgetMax", label: "Max Budget" },
    { value: "specialRequests", label: "Special Requests" },
  ];

  return (
    <Modal
      title="Bulk Query Import"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <FileExcelOutlined />
              CSV/Excel Import;
            </span>
          }
          key="csv"
        >
          <Form form={form} layout="vertical" onFinish={handleCSVImport}>
            <Card
              title="Upload CSV File"
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={downloadTemplate}
                  type="dashed"
                >
                  Download Template;
                </Button>

                <Dragger {...csvUploadProps}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag CSV/Excel file to upload
                  </p>
                  <p className="ant-upload-hint">
                    Support for CSV, XLSX, XLS files. Maximum file size: 10MB;
                  </p>
                </Dragger>
              </Space>
            </Card>

            {csvPreview.length > 0 && (
              <Card
                title="Preview & Field Mapping"
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Table
                  dataSource={csvPreview.slice(1).map((row, index) => ({
                    key: index,
                    ...row.reduce((obj, cell, cellIndex) => {
                      obj[`col${cellIndex}`] = cell;
                      return obj;
                    }, {}),
                  }))}
                  columns={
                    csvPreview[0]?.map((header, index) => ({
                      title: (
                        <div>
                          <div>{header}</div>
                          <Select
                            size="small"
                            placeholder="Map field"
                            value={fieldMapping[index]}
                            onChange={(value) =>
                              setFieldMapping({
                                ...fieldMapping,
                                [index]: value,
                              })
                            }
                            style={{ width: "100%", marginTop: 4 }}
                          >
                            {fieldOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      ),
                      dataIndex: `col${index}`,
                      key: index,
                      width: 150,
                    })) || []
                  }
                  pagination={false}
                  scroll={{ x: true }}
                  size="small"
                />
              </Card>
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="assignToTeam" label="Assign to Team">
                  <Select placeholder="Select team (optional)">
                    {teams.map((team) => (
                      <Option key={team._id} value={team._id}>
                        {team.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="assignToUser" label="Assign to User">
                  <Select placeholder="Select user (optional)">
                    {users.map((user) => (
                      <Option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="hierarchyAssignment" valuePropName="checked">
                  <input type="checkbox" /> Auto-assign by hierarchy;
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="skipDuplicates" valuePropName="checked">
                  <input type="checkbox" /> Skip duplicates;
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="updateExisting" valuePropName="checked">
                  <input type="checkbox" /> Update existing;
                </Form.Item>
              </Col>
            </Row>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <Progress percent={uploadProgress} status="active" />
            )}

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Import Queries;
                </Button>
                <Button onClick={onClose}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane
          tab={
            <span>
              <GoogleOutlined />
              Google Sheets;
            </span>
          }
          key="sheets"
        >
          <Form
            form={googleForm}
            layout="vertical"
            onFinish={handleGoogleSheetsConnect}
          >
            <Alert
              message="Google Sheets Integration"
              description="Connect your Google Sheets for real-time query import and synchronization."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              name="spreadsheetId"
              label="Spreadsheet ID"
              rules={[
                { required: true, message: "Please enter spreadsheet ID" },
              ]}
            >
              <Input placeholder="Enter Google Sheets ID from URL" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="sheetName"
                  label="Sheet Name"
                  rules={[
                    { required: true, message: "Please enter sheet name" },
                  ]}
                >
                  <Input placeholder="e.g., Sheet1" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="range" label="Range (Optional)">
                  <Input placeholder="e.g., A1:K1000" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="assignToTeam" label="Assign to Team">
                  <Select placeholder="Select team (optional)">
                    {teams.map((team) => (
                      <Option key={team._id} value={team._id}>
                        {team.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="assignToUser" label="Assign to User">
                  <Select placeholder="Select user (optional)">
                    {users.map((user) => (
                      <Option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="hierarchyAssignment" valuePropName="checked">
                  <input type="checkbox" /> Auto-assign by hierarchy
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="autoSync" valuePropName="checked">
                  <input type="checkbox" /> Enable auto-sync
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="syncFrequency" label="Sync Frequency">
                  <Select defaultValue="daily">
                    <Option value="hourly">Hourly</Option>
                    <Option value="daily">Daily</Option>
                    <Option value="weekly">Weekly</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Connect Google Sheets
                </Button>
                <Button onClick={onClose}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>

      {importStatus && (
        <Alert
          message={importStatus.message}
          type={importStatus.type}
          showIcon
          style={{ marginTop: 16 }}
          action={
            importStatus.type === "success" && (
              <Button size="small" onClick={() => setImportStatus(null)}>
                Close
              </Button>
            )
          }
        />
      )}
    </Modal>
  );
};

export default BulkQueryImportModal;
